/**
 * Password Validation Utilities
 * 
 * Enforces strong password policy: min 12 chars, denylist, strength indicator
 */

// Common passwords denylist (case-insensitive check)
const COMMON_PASSWORDS = [
  'password', 'password1', 'password123', 'password1234',
  '12345678', '123456789', '1234567890', '12345678901234',
  'qwerty', 'qwertyuiop', 'qwerty123',
  'letmein', 'welcome', 'welcome1', 'welcome123',
  'admin', 'admin123', 'administrator',
  'changeme', 'changeme123',
  'abc123', 'abcdef', 'abcd1234',
  'iloveyou', 'sunshine', 'princess', 'monkey',
  'dragon', 'master', 'login', 'passw0rd',
  'baseball', 'football', 'soccer', 'hockey',
  'radiology', 'radiologist', 'doctor', 'medical',
];

// Sequential/repeated patterns to reject
const WEAK_PATTERNS = [
  /^(.)\1+$/,          // All same character (aaaaaaaaaa)
  /^(12)+$/,           // Repeating 12
  /^(abc)+$/i,         // Repeating abc
  /^0123456789/,       // Sequential numbers
  /^9876543210/,       // Reverse sequential
  /^abcdefghij/i,      // Sequential letters
];

export interface PasswordValidation {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'fair' | 'good' | 'strong';
  score: number; // 0-100
}

export interface PasswordRequirement {
  id: string;
  label: string;
  met: boolean;
}

/**
 * Get individual password requirements with live status
 * Used for the requirements checklist UI
 */
export function getPasswordRequirements(
  password: string,
  confirmPassword?: string,
  email?: string
): PasswordRequirement[] {
  const lowerPassword = password.toLowerCase();
  const emailLocal = email ? email.split('@')[0].toLowerCase() : null;

  // Check if password is in common denylist
  const isCommonPassword = COMMON_PASSWORDS.some(common =>
    lowerPassword.includes(common)
  );

  // Check if password contains email
  const containsEmail = emailLocal && emailLocal.length >= 3 &&
    lowerPassword.includes(emailLocal);

  return [
    {
      id: 'length',
      label: 'At least 12 characters',
      met: password.length >= 12,
    },
    {
      id: 'lowercase',
      label: 'Contains lowercase letter',
      met: /[a-z]/.test(password),
    },
    {
      id: 'uppercase',
      label: 'Contains uppercase letter',
      met: /[A-Z]/.test(password),
    },
    {
      id: 'number',
      label: 'Contains number',
      met: /[0-9]/.test(password),
    },
    {
      id: 'special',
      label: 'Contains special character',
      met: /[^a-zA-Z0-9]/.test(password),
    },
    {
      id: 'notCommon',
      label: 'Not a common password',
      met: password.length > 0 && !isCommonPassword,
    },
    ...(email ? [{
      id: 'noEmail',
      label: 'Does not include email',
      met: password.length > 0 && !containsEmail,
    }] : []),
    ...(confirmPassword !== undefined ? [{
      id: 'match',
      label: 'Passwords match',
      met: password.length > 0 && confirmPassword.length > 0 && password === confirmPassword,
    }] : []),
  ];
}

/**
 * Calculate password strength score
 */
function calculateStrengthScore(password: string): number {
  let score = 0;
  
  // Length scoring (up to 40 points)
  if (password.length >= 12) score += 20;
  if (password.length >= 16) score += 10;
  if (password.length >= 20) score += 10;
  
  // Character variety (up to 40 points)
  if (/[a-z]/.test(password)) score += 10;
  if (/[A-Z]/.test(password)) score += 10;
  if (/[0-9]/.test(password)) score += 10;
  if (/[^a-zA-Z0-9]/.test(password)) score += 10;
  
  // Bonus for mixed patterns (up to 20 points)
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSpecial = /[^a-zA-Z0-9]/.test(password);
  
  const categoryCount = [hasLower, hasUpper, hasDigit, hasSpecial].filter(Boolean).length;
  if (categoryCount >= 3) score += 10;
  if (categoryCount === 4) score += 10;
  
  return Math.min(100, score);
}

/**
 * Get strength label from score
 */
function getStrengthLabel(score: number): 'weak' | 'fair' | 'good' | 'strong' {
  if (score < 40) return 'weak';
  if (score < 60) return 'fair';
  if (score < 80) return 'good';
  return 'strong';
}

/**
 * Validate password against security requirements
 */
export function validatePassword(password: string, email?: string): PasswordValidation {
  const errors: string[] = [];
  
  // Minimum length check (12 characters)
  if (password.length < 12) {
    errors.push('Password must be at least 12 characters.');
  }
  
  // Maximum length check (prevent DOS)
  if (password.length > 128) {
    errors.push('Password must be less than 128 characters.');
  }
  
  // Common password denylist check
  const lowerPassword = password.toLowerCase();
  if (COMMON_PASSWORDS.some(common => lowerPassword.includes(common))) {
    errors.push('Password is too common. Please choose a more unique password.');
  }
  
  // Weak pattern check
  if (WEAK_PATTERNS.some(pattern => pattern.test(password))) {
    errors.push('Password contains a weak pattern. Avoid repeated or sequential characters.');
  }
  
  // Check if password contains email local-part
  if (email) {
    const emailLocal = email.split('@')[0].toLowerCase();
    if (emailLocal.length >= 3 && lowerPassword.includes(emailLocal)) {
      errors.push('Password should not contain your email address.');
    }
  }
  
  const score = calculateStrengthScore(password);
  const strength = getStrengthLabel(score);
  
  return {
    isValid: errors.length === 0,
    errors,
    strength,
    score,
  };
}

/**
 * Get color class for strength indicator
 */
export function getStrengthColor(strength: 'weak' | 'fair' | 'good' | 'strong'): string {
  switch (strength) {
    case 'weak': return 'text-red-500';
    case 'fair': return 'text-yellow-500';
    case 'good': return 'text-blue-500';
    case 'strong': return 'text-green-500';
  }
}

/**
 * Get background color class for strength bar
 */
export function getStrengthBgColor(strength: 'weak' | 'fair' | 'good' | 'strong'): string {
  switch (strength) {
    case 'weak': return 'bg-red-500';
    case 'fair': return 'bg-yellow-500';
    case 'good': return 'bg-blue-500';
    case 'strong': return 'bg-green-500';
  }
}

/**
 * Map Supabase auth error codes to user-friendly messages
 * Prevents leaking internal system details
 */
export function getAuthErrorMessage(error: { message?: string; code?: string }): string {
  const code = error.code || '';
  const message = error.message?.toLowerCase() || '';
  
  // Map known error codes/messages to safe user messages
  if (code === 'invalid_credentials' || message.includes('invalid login')) {
    return 'Invalid email or password.';
  }
  if (code === 'email_not_confirmed' || message.includes('email not confirmed')) {
    return 'Please verify your email address before signing in.';
  }
  if (code === 'user_banned' || message.includes('banned')) {
    return 'This account has been disabled. Contact support.';
  }
  if (code === 'user_already_exists' || message.includes('already registered')) {
    return 'An account with this email already exists.';
  }
  if (code === 'weak_password' || message.includes('weak password')) {
    return 'Password does not meet security requirements.';
  }
  if (message.includes('rate limit') || message.includes('too many')) {
    return 'Too many attempts. Please try again later.';
  }
  if (message.includes('network') || message.includes('fetch')) {
    return 'Network error. Please check your connection.';
  }
  
  // Generic fallback - don't expose internal details
  return 'Authentication failed. Please try again.';
}
